import { API_BASE_URL } from "@/lib/api";
import { auth } from "@/lib/firebaseClient";

/** The next request to resume from when a stream closes before `[DONE]`. */
export interface SseReconnect {
  path: string;
  method?: "GET" | "POST";
  body?: unknown;
}

export interface SseTransportOptions {
  path: string;
  /** HTTP method for the first request. Defaults to `POST`. */
  method?: "GET" | "POST";
  body?: unknown;
  /** Called with each non-`[DONE]` `data:` payload; `eventId` is the frame's `id:` field, when present. */
  onData: (data: string, eventId?: string) => void;
  /** Called on clean completion (`[DONE]`), abort, or a clean close with no reconnect target — but NOT when `onError` fires. */
  onDone?: () => void;
  /** Called when a non-resumable error terminates the loop; `done` then rejects and `onDone` is not called. */
  onError?: (error: Error) => void;
  /**
   * Called when a connection drops before `[DONE]` and a reconnect is about to be
   * attempted (`reconnecting=true`), and again once a frame is received on the
   * resumed connection (`reconnecting=false`).
   */
  onReconnecting?: (reconnecting: boolean) => void;
  signal?: AbortSignal;
  /** Initial `Last-Event-ID` (resume cursor) sent on the first request. */
  lastEventId?: string | null;
  /**
   * Called when the stream closes (clean close or transient error) before `[DONE]`.
   * Return the next request to resume from — the latest seen id is sent as the
   * `Last-Event-ID` header — or `null` to stop without reconnecting.
   *
   * The consecutive-error budget applies only to error-path reconnects; clean closes
   * resume as long as this returns non-null, so implementations that always return a
   * target are responsible for imposing any finite reconnect budget themselves.
   */
  resolveReconnect?: (lastEventId: string | null) => SseReconnect | null;
}

export interface SseHandle {
  cancel: () => void;
  done: Promise<void>;
  controller: AbortController;
}

interface SseFrame {
  /** The `id:` field of the frame (a `chat_events` id), if present — the resume cursor. */
  id: string | null;
  /** The joined `data:` payload of the frame, if present. */
  data: string | null;
}

function parseSseBlock(block: string): SseFrame {
  let id: string | null = null;
  const dataLines: string[] = [];
  for (const rawLine of block.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    } else if (line.startsWith("id:")) {
      id = line.slice(3).trim() || null;
    }
  }
  return { id, data: dataLines.length ? dataLines.join("\n") : null };
}

const RECONNECT_DELAY_MS = 400;
const MAX_CONSECUTIVE_RECONNECT_ERRORS = 5;

function sseDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function streamUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${API_BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

interface SseTarget {
  path: string;
  method: "GET" | "POST";
  body?: unknown;
}

async function openSseResponse(target: SseTarget, lastEventId: string | null, signal: AbortSignal): Promise<Response> {
  const currentUser = auth.currentUser;
  const token = currentUser ? await currentUser.getIdToken() : null;

  const response = await fetch(streamUrl(target.path), {
    method: target.method,
    cache: "no-store",
    headers: {
      Accept: "text/event-stream",
      ...(target.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(lastEventId ? { "Last-Event-ID": lastEventId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: target.body === undefined ? undefined : JSON.stringify(target.body),
    signal
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Stream error: ${response.status}`);
  }

  if (!response.body) {
    throw new Error("Streaming response did not include a body");
  }

  return response;
}

/** Read one connection to completion; returns true once `[DONE]` is seen. */
async function readSseConnection(response: Response, handleFrame: (frame: SseFrame) => "done" | "continue"): Promise<boolean> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawDone = false;

  while (!sawDone) {
    const { value, done: readerDone } = await reader.read();
    if (readerDone) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() ?? "";

    for (const block of blocks) {
      if (handleFrame(parseSseBlock(block)) === "done") {
        sawDone = true;
        break;
      }
    }
  }

  if (!sawDone && handleFrame(parseSseBlock(buffer)) === "done") {
    sawDone = true;
  }

  await reader.cancel().catch(() => {});
  return sawDone;
}

interface LoopState {
  target: SseTarget;
  lastEventId: string | null;
  consecutiveErrors: number;
}

type ReconnectOutcome = "resume" | "stop" | "aborted";

/** Decide whether to resume, retarget the loop, and wait out the backoff delay. */
async function prepareReconnect(state: LoopState, options: SseTransportOptions, signal: AbortSignal, enforceErrorLimit: boolean): Promise<ReconnectOutcome> {
  const next = options.resolveReconnect?.(state.lastEventId) ?? null;
  if (!next || (enforceErrorLimit && state.consecutiveErrors > MAX_CONSECUTIVE_RECONNECT_ERRORS)) {
    return "stop";
  }
  options.onReconnecting?.(true);
  state.target = { path: next.path, method: next.method ?? "GET", body: next.body };
  await sseDelay(RECONNECT_DELAY_MS, signal);
  return signal.aborted ? "aborted" : "resume";
}

/**
 * Resumable SSE fetch loop: each connection runs until `[DONE]`. On a clean close or a
 * transient error before `[DONE]`, `resolveReconnect` (when provided) yields the next
 * request and the loop resumes with the latest seen id as `Last-Event-ID`.
 *
 * @internal Prefer the `stream.ts` helpers (`streamChat`, `streamProjectChat`,
 * `streamChatLive`); call this directly only when raw `data:` payloads are needed
 * without stream-event normalization.
 */
export function startSseTransport(options: SseTransportOptions): SseHandle {
  const controller = new AbortController();

  const forwardAbort = () => controller.abort();
  options.signal?.addEventListener("abort", forwardAbort, { once: true });

  const state: LoopState = {
    target: { path: options.path, method: options.method ?? "POST", body: options.body },
    lastEventId: options.lastEventId ?? null,
    consecutiveErrors: 0
  };

  const done = (async () => {
    try {
      while (true) {
        let receivedFrame = false;

        // Returns "done" once `[DONE]` is seen; updates the resume cursor on `id:` frames.
        const handleFrame = (frame: SseFrame): "done" | "continue" => {
          if (!receivedFrame) {
            // First frame on this (re)connection — including a heartbeat comment —
            // means the connection is alive; clear any "reconnecting" state.
            options.onReconnecting?.(false);
          }
          receivedFrame = true;
          if (frame.id) {
            state.lastEventId = frame.id;
          }
          if (!frame.data) {
            return "continue";
          }
          if (frame.data === "[DONE]") {
            return "done";
          }
          options.onData(frame.data, frame.id ?? undefined);
          return "continue";
        };

        let sawDone = false;
        try {
          const response = await openSseResponse(state.target, state.lastEventId, controller.signal);
          sawDone = await readSseConnection(response, handleFrame);
        } catch (error) {
          if (controller.signal.aborted) {
            options.onDone?.();
            return;
          }

          state.consecutiveErrors = receivedFrame ? 0 : state.consecutiveErrors + 1;
          const outcome = await prepareReconnect(state, options, controller.signal, true);
          if (outcome === "resume") {
            continue;
          }
          if (outcome === "aborted") {
            options.onDone?.();
            return;
          }

          const normalized = error instanceof Error ? error : new Error("Unknown stream error");
          options.onError?.(normalized);
          throw normalized;
        }

        if (sawDone || controller.signal.aborted) {
          options.onDone?.();
          return;
        }

        // Clean close before `[DONE]` (dropped connection): resume if a target is available.
        if (receivedFrame) {
          state.consecutiveErrors = 0;
        }
        const outcome = await prepareReconnect(state, options, controller.signal, false);
        if (outcome !== "resume") {
          options.onDone?.();
          return;
        }
      }
    } finally {
      options.signal?.removeEventListener("abort", forwardAbort);
    }
  })();

  return {
    controller,
    done,
    cancel: () => controller.abort()
  };
}
